import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { type GoogleComment } from '../../lib/googleWorkspace';

interface GoogleCommentsImportModalProps {
  jobId: string;
  documentId: string;
  scriptId?: string;
  isOpen: boolean;
  onClose: () => void;
  onImported?: () => void;
}

export function GoogleCommentsImportModal({
  jobId,
  documentId,
  scriptId,
  isOpen,
  onClose,
  onImported,
}: GoogleCommentsImportModalProps) {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<GoogleComment[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<{ comments: GoogleComment[]; demo?: boolean }>(
          `/google-workspace/jobs/${jobId}/documents/${documentId}/comments`
        );
        if (cancelled) return;
        const list = res.data.comments ?? [];
        setComments(list);
        setDemo(Boolean(res.data.demo));
        setSelected(
          new Set(list.filter((c) => !c.alreadyImported && !c.resolved).map((c) => c.id))
        );
      } catch (err: unknown) {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to load comments';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, documentId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    if (!scriptId || selected.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      await api.post(
        `/google-workspace/jobs/${jobId}/documents/${documentId}/import-comments`,
        { scriptId, commentIds: [...selected] }
      );
      onImported?.();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to import comments';
      setError(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Google comments"
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            {scriptId
              ? `${selected.size} comment${selected.size === 1 ? '' : 's'} to import as brand notes`
              : 'Link a script to import comments'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={importing}>
              Close
            </Button>
            {scriptId && (
              <Button
                onClick={() => void handleImport()}
                disabled={importing || loading || selected.size === 0}
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Import to script'
                )}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      )}

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      {!loading && (
        <div className="space-y-3">
          {demo && (
            <Badge variant="secondary">Demo mode — connect Google OAuth for live comments</Badge>
          )}
          {!scriptId && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded p-3">
              Open a script workspace to import comments into the revision workflow.
            </p>
          )}
          {comments.length === 0 ? (
            <p className="text-sm text-gray-600 py-6 text-center">No comments found on this file.</p>
          ) : (
            comments.map((c) => (
              <label
                key={c.id}
                className={`flex gap-3 rounded-lg border p-3 cursor-pointer ${
                  c.alreadyImported ? 'opacity-60' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(c.id)}
                  disabled={c.alreadyImported || c.resolved || !scriptId}
                  onChange={() => toggle(c.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{c.author}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(c.createdAt).toLocaleString()}
                    </span>
                    {c.resolved && <Badge variant="outline">Resolved</Badge>}
                    {c.alreadyImported && <Badge variant="secondary">Imported</Badge>}
                  </div>
                  {c.anchorText && (
                    <p className="text-xs text-gray-500 mb-1 italic">Re: {c.anchorText}</p>
                  )}
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.content}</p>
                </div>
              </label>
            ))
          )}
        </div>
      )}
    </Modal>
  );
}
